import type { ListChip } from './conversationList';

/**
 * URL state of the Verläufe page. The URL is the source of truth for what is
 * open, so links work from the bell, the dashboard and the transfer banner,
 * and the back button closes a chat on the phone.
 *
 *   ?contact=<contact_key>   WhatsApp contact chat (new)
 *   ?call=<call_sid>         live phone call (new)
 *   ?c=<conversation_id>     one stored conversation (old links, phone calls)
 *   focus=composer           put the cursor into the reply box once
 *   ?type=phone|whatsapp     channel chip · ?filter=live|waiting|taken other chips
 *   ?q=<text>                search
 *   ?phone=&at=              jump to a number (dashboard, transfer banner)
 */

export type Selection =
  | { kind: 'contact'; key: string }
  | { kind: 'call'; sid: string }
  | { kind: 'conversation'; id: number };

const SELECTION_PARAMS = ['contact', 'call', 'c', 'focus'] as const;

export function parseSelection(params: URLSearchParams): Selection | null {
  const contact = params.get('contact')?.trim();
  if (contact) return { kind: 'contact', key: contact };
  const call = params.get('call')?.trim();
  if (call) return { kind: 'call', sid: call };
  const c = params.get('c');
  if (c && /^\d+$/.test(c)) return { kind: 'conversation', id: Number(c) };
  return null;
}

export function sameSelection(a: Selection | null, b: Selection | null): boolean {
  if (!a || !b) return a === b;
  if (a.kind === 'contact' && b.kind === 'contact') return a.key === b.key;
  if (a.kind === 'call' && b.kind === 'call') return a.sid === b.sid;
  if (a.kind === 'conversation' && b.kind === 'conversation') return a.id === b.id;
  return false;
}

/** Same params with `selection` open instead of whatever was open (filters stay). */
export function withSelection(
  params: URLSearchParams,
  selection: Selection | null,
  opts: { focusComposer?: boolean } = {},
): URLSearchParams {
  const next = new URLSearchParams(params);
  SELECTION_PARAMS.forEach((p) => next.delete(p));
  if (selection?.kind === 'contact') next.set('contact', selection.key);
  if (selection?.kind === 'call') next.set('call', selection.sid);
  if (selection?.kind === 'conversation') next.set('c', String(selection.id));
  if (selection && opts.focusComposer) next.set('focus', 'composer');
  return next;
}

export function wantsComposerFocus(params: URLSearchParams): boolean {
  return params.get('focus') === 'composer';
}

export function withoutComposerFocus(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('focus');
  return next;
}

export function parseChip(params: URLSearchParams): ListChip {
  const type = params.get('type');
  if (type === 'phone' || type === 'whatsapp') return type;
  const filter = params.get('filter');
  if (filter === 'live' || filter === 'waiting' || filter === 'taken') return filter;
  return 'all';
}

/** Chip + search into the params; the open selection stays untouched. */
export function withListFilters(params: URLSearchParams, chip: ListChip, query: string): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('type');
  next.delete('filter');
  if (chip === 'phone' || chip === 'whatsapp') next.set('type', chip);
  else if (chip !== 'all') next.set('filter', chip);
  const q = query.trim();
  if (q) next.set('q', q);
  else next.delete('q');
  return next;
}

export interface JumpRequest {
  phone: string;
  at: string | null;
  chip: ListChip;
}

/** `?phone=&at=&type=` from the dashboard or the transfer banner. */
export function parseJump(params: URLSearchParams): JumpRequest | null {
  const phone = params.get('phone')?.trim();
  if (!phone) return null;
  const type = params.get('type');
  return {
    phone,
    at: params.get('at'),
    chip: type === 'phone' || type === 'whatsapp' ? type : 'all',
  };
}

export function withoutJump(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('phone');
  next.delete('at');
  return next;
}
